const MOBILE_QUERY = '(max-width: 989px)';
const PANEL_DURATION = 240;
const PANEL_EASING = 'cubic-bezier(.22, 1, .36, 1)';
const animations = new WeakMap();

function isMobile() {
  return window.matchMedia(MOBILE_QUERY).matches;
}

function cancelAnimation(panel) {
  const animation = animations.get(panel);
  if (!animation) return;

  animation.cancel();
  animations.delete(panel);
}

function setPanelState(trigger, panel, opening, immediate = false) {
  cancelAnimation(panel);

  if (immediate) {
    trigger.setAttribute('aria-expanded', opening ? 'true' : 'false');
    panel.hidden = !opening;
    panel.removeAttribute('style');
    return;
  }

  if (opening) panel.hidden = false;

  const fromHeight = opening ? 0 : panel.getBoundingClientRect().height;
  const toHeight = opening ? panel.scrollHeight : 0;

  panel.style.overflow = 'hidden';

  const animation = panel.animate(
    [
      {
        height: `${fromHeight}px`,
        opacity: opening ? 0 : 1,
        transform: opening ? 'translateY(-4px)' : 'translateY(0)',
      },
      {
        height: `${toHeight}px`,
        opacity: opening ? 1 : 0,
        transform: opening ? 'translateY(0)' : 'translateY(-3px)',
      },
    ],
    {
      duration: PANEL_DURATION,
      easing: PANEL_EASING,
      fill: 'both',
    }
  );

  animations.set(panel, animation);
  trigger.setAttribute('aria-expanded', opening ? 'true' : 'false');

  animation.addEventListener(
    'finish',
    () => {
      animations.delete(panel);
      panel.removeAttribute('style');
      if (!opening) panel.hidden = true;
    },
    { once: true }
  );

  animation.addEventListener(
    'cancel',
    () => {
      animations.delete(panel);
    },
    { once: true }
  );
}

function closeOtherPanels(menu, currentTrigger = null, immediate = false) {
  menu
    .querySelectorAll('[data-xh-accordion-trigger-v5][aria-expanded="true"]')
    .forEach((trigger) => {
      if (trigger === currentTrigger) return;

      const panelId = trigger.getAttribute('aria-controls');
      const panel = panelId ? document.getElementById(panelId) : null;
      if (panel) setPanelState(trigger, panel, false, immediate);
    });
}

function initializeHeader(header) {
  if (!(header instanceof HTMLElement)) return;
  if (header.dataset.xhMobileHeaderV5Ready === 'true') return;

  header.dataset.xhMobileHeaderV5Ready = 'true';

  const searchToggle = header.querySelector('[data-xh-mobile-search-toggle-v41]');

  /*
   * Ausschließlich das aufklappbare Suchfeld der normalen Header-Zeile
   * auswählen. Das Predictive-Search-Feld innerhalb des mobilen Drawers
   * darf hier nicht verwendet werden.
   */
  const predictiveSearch = header.querySelector(
    '.xh-predictive-search[data-xh-predictive-search]'
  );
  const predictiveInput = predictiveSearch?.querySelector('[data-xh-search-input]');
  const predictiveResults = predictiveSearch?.querySelector('[data-xh-search-results]');
  const drawer = header.querySelector('#Details-menu-drawer-container');
  const drawerSummary = drawer?.querySelector(':scope > summary');
  const menu = header.querySelector('[data-xh-mobile-menu-v5]');
  const drawerSearchForm = menu?.querySelector('[data-xh-mobile-search-form-v5]');
  const drawerSearchInput = menu?.querySelector('[data-xh-mobile-search-input-v5]');
  const drawerAccountTrigger = menu?.querySelector('[data-xh-account-trigger-v5]');

  const initializeSearchClearButtons = () => {
    header.querySelectorAll('[data-xh-predictive-search]').forEach((root) => {
      const input = root.querySelector('[data-xh-search-input]');
      const results = root.querySelector('[data-xh-search-results]');
      const form = input?.closest('form');

      if (!(input instanceof HTMLInputElement) || !(form instanceof HTMLFormElement)) {
        return;
      }

      let clearButton = form.querySelector('[data-xh-search-clear]');

      if (!(clearButton instanceof HTMLButtonElement)) {
        clearButton = document.createElement('button');
        clearButton.type = 'button';
        clearButton.className = 'xh-search-clear';
        clearButton.setAttribute('data-xh-search-clear', '');
        clearButton.setAttribute('aria-label', 'Sucheingabe löschen');
        clearButton.innerHTML = `
          <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            <path d="M6.5 6.5 17.5 17.5"></path>
            <path d="M17.5 6.5 6.5 17.5"></path>
          </svg>
        `;

        const submitButton = form.querySelector('button[type="submit"]');
        form.insertBefore(clearButton, submitButton || null);
      }

      const syncClearButton = () => {
        clearButton.hidden = input.value.length === 0;
      };

      input.addEventListener('input', syncClearButton);

      clearButton.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();

        input.value = '';

        /*
         * Die bestehende Predictive-Search-Logik erhält dasselbe Input-Event
         * wie bei einer manuellen Eingabe und schließt dadurch ihre Treffer.
         */
        input.dispatchEvent(new Event('input', { bubbles: true }));

        if (results instanceof HTMLElement) {
          results.hidden = true;
          results.innerHTML = '';
        }

        syncClearButton();
        input.focus({ preventScroll: true });
      });

      syncClearButton();
    });
  };

  initializeSearchClearButtons();

  const setPredictiveSearchOpen = (open) => {
    if (!isMobile()) open = false;

    header.classList.toggle('xh-mobile-search-open-v41', open);
    document.body.classList.toggle('xh-mobile-search-open-v41', open);

    searchToggle?.setAttribute('aria-expanded', open ? 'true' : 'false');
    searchToggle?.setAttribute('aria-label', open ? 'Suche schließen' : 'Suche öffnen');

    if (!open) {
      predictiveInput?.blur();
      if (predictiveResults) predictiveResults.hidden = true;
      return;
    }

    requestAnimationFrame(() => {
      predictiveInput?.focus({ preventScroll: true });
    });
  };

  const syncDrawerState = () => {
    const open = Boolean(isMobile() && drawer?.open);

    document.body.classList.toggle('xh-mobile-drawer-open-v5', open);
    header.classList.toggle('xh-mobile-drawer-open-v5', open);

    if (open) {
      setPredictiveSearchOpen(false);
      return;
    }

    if (menu) closeOtherPanels(menu, null, true);
  };

  searchToggle?.addEventListener('click', (event) => {
    if (!isMobile()) return;

    event.preventDefault();
    event.stopPropagation();

    const opening = !header.classList.contains('xh-mobile-search-open-v41');

    if (opening && drawer?.open) {
      drawerSummary?.click();
    }

    setPredictiveSearchOpen(opening);
  });

  drawer?.addEventListener('toggle', syncDrawerState);

  const wait = (milliseconds) =>
    new Promise((resolve) => window.setTimeout(resolve, milliseconds));

  const findNativeAccountComponent = () => {
    const accountComponents = Array.from(
      header.querySelectorAll('.account-button shopify-account')
    );

    return (
      accountComponents.find((component) => {
        const accountButton = component.closest('.account-button');
        return accountButton && accountButton.getClientRects().length > 0;
      }) ||
      accountComponents[0] ||
      null
    );
  };

  const openNativeShopifyAccount = async () => {
    try {
      if (window.customElements?.whenDefined) {
        await window.customElements.whenDefined('shopify-account');
      }
    } catch (error) {
      /* Der Fallback-Link übernimmt, falls das Shopify-Element nicht lädt. */
    }

    for (let attempt = 0; attempt < 24; attempt += 1) {
      const accountComponent = findNativeAccountComponent();
      const shadowTrigger =
        accountComponent?.shadowRoot?.querySelector(
          'button, a, [role="button"]'
        );

      if (shadowTrigger instanceof HTMLElement) {
        shadowTrigger.click();
        return true;
      }

      await wait(50);
    }

    return false;
  };

  drawerAccountTrigger?.addEventListener('click', async (event) => {
    event.preventDefault();
    event.stopPropagation();

    drawerAccountTrigger.classList.add('is-pressed');

    window.setTimeout(() => {
      drawerAccountTrigger.classList.remove('is-pressed');
    }, 190);

    /*
     * Der mobile Drawer bleibt geöffnet. Das native Shopify-Konto-Popover
     * wird direkt darüber ausgelöst, sodass die Seite hinter dem Drawer
     * nicht mehr sichtbar wird.
     */
    const opened = await openNativeShopifyAccount();

    if (!opened) {
      const fallbackUrl = drawerAccountTrigger.dataset.accountUrl;
      if (fallbackUrl) window.location.assign(fallbackUrl);
    }
  });

  drawerSearchForm?.addEventListener('submit', (event) => {
    const query = drawerSearchInput?.value.trim() ?? '';

    if (!query) {
      event.preventDefault();
      drawerSearchForm.classList.remove('is-empty');
      void drawerSearchForm.offsetWidth;
      drawerSearchForm.classList.add('is-empty');
      drawerSearchInput?.focus({ preventScroll: true });

      window.setTimeout(() => {
        drawerSearchForm.classList.remove('is-empty');
      }, 320);
      return;
    }

    event.preventDefault();

    const target = new URL(drawerSearchForm.action, window.location.origin);
    const formData = new FormData(drawerSearchForm);

    formData.forEach((value, key) => {
      const normalizedValue = String(value).trim();
      if (normalizedValue) target.searchParams.append(key, normalizedValue);
    });

    window.location.assign(target.toString());
  });

  menu?.addEventListener('pointerdown', (event) => {
    const feedbackTarget = event.target.closest(
      '.xh-mobile-menu-v5__item--secondary > .xh-mobile-menu-v5__trigger, ' +
        '.xh-mobile-menu-v5__standalone--primary > a, ' +
        '.xh-mobile-menu-v5__standalone--secondary > a, ' +
        '.xh-mobile-menu-v5__child, ' +
        '.xh-mobile-menu-v5__grandchildren a, ' +
        '.xh-mobile-menu-v5__utility-link > a, ' +
        '.xh-mobile-menu-v5__utility-link > button'
    );

    if (feedbackTarget instanceof HTMLElement) {
      feedbackTarget.classList.add('is-pressed');
    }
  });

  const clearPressedFeedback = () => {
    menu?.querySelectorAll('.is-pressed').forEach((element) => {
      element.classList.remove('is-pressed');
    });
  };

  menu?.addEventListener('pointerup', () => {
    window.setTimeout(clearPressedFeedback, 150);
  });

  menu?.addEventListener('pointercancel', clearPressedFeedback);

  menu?.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-xh-accordion-trigger-v5]');

    if (trigger instanceof HTMLButtonElement) {
      event.preventDefault();

      const panelId = trigger.getAttribute('aria-controls');
      const panel = panelId ? document.getElementById(panelId) : null;
      if (!panel) return;

      const opening = trigger.getAttribute('aria-expanded') !== 'true';

      closeOtherPanels(menu, trigger);
      setPanelState(trigger, panel, opening);
      return;
    }

    const feedbackLink = event.target.closest(
      '.xh-mobile-menu-v5__standalone--primary > a, ' +
        '.xh-mobile-menu-v5__standalone--secondary > a, ' +
        '.xh-mobile-menu-v5__child, ' +
        '.xh-mobile-menu-v5__grandchildren a, ' +
        '.xh-mobile-menu-v5__utility-link > a'
    );

    if (!(feedbackLink instanceof HTMLAnchorElement)) return;
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey ||
      feedbackLink.target === '_blank' ||
      feedbackLink.hasAttribute('download')
    ) {
      return;
    }

    const targetUrl = new URL(feedbackLink.href, window.location.href);
    if (targetUrl.origin !== window.location.origin) return;

    event.preventDefault();
    feedbackLink.classList.add('is-pressed');

    window.setTimeout(() => {
      window.location.assign(targetUrl.toString());
    }, 140);
  });

  document.addEventListener('click', (event) => {
    if (!isMobile()) return;
    if (!header.classList.contains('xh-mobile-search-open-v41')) return;

    /*
     * composedPath ist in Safari zuverlässiger als contains(event.target),
     * insbesondere bei verschachtelten Theme-/Custom-Elementen.
     */
    const eventPath =
      typeof event.composedPath === 'function' ? event.composedPath() : [];

    if (
      (predictiveSearch && eventPath.includes(predictiveSearch)) ||
      (searchToggle && eventPath.includes(searchToggle))
    ) {
      return;
    }

    const target = event.target;
    if (!(target instanceof Node)) return;

    if (predictiveSearch?.contains(target) || searchToggle?.contains(target)) {
      return;
    }

    setPredictiveSearchOpen(false);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape' || !isMobile()) return;

    if (header.classList.contains('xh-mobile-search-open-v41')) {
      setPredictiveSearchOpen(false);
      searchToggle?.focus();
    }
  });

  window.matchMedia(MOBILE_QUERY).addEventListener('change', (event) => {
    if (event.matches) {
      syncDrawerState();
      return;
    }

    setPredictiveSearchOpen(false);
    document.body.classList.remove('xh-mobile-drawer-open-v5');
    header.classList.remove('xh-mobile-drawer-open-v5');
    if (menu) closeOtherPanels(menu, null, true);
  });

  document.addEventListener('shopify:section:load', initializeSearchClearButtons);

  syncDrawerState();
}

function initializeAll(scope = document) {
  scope.querySelectorAll?.('#header-component').forEach(initializeHeader);

  if (scope instanceof HTMLElement && scope.matches('#header-component')) {
    initializeHeader(scope);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initializeAll(), { once: true });
} else {
  initializeAll();
}

document.addEventListener('shopify:section:load', (event) => {
  initializeAll(event.target);
});